import { retrieveToken } from '../storage';
import {
  wrapFetch,
  wrapFetchWithDetailedErrors,
  apiFetch,
  apiError,
} from './api';

const BASE_URL = process.env.REACT_APP_BACKEND_ENDPOINT;

/**
 * @param {{soloPubblicati?: boolean}} [opzioni] `soloPubblicati` asks the server
 *   for the reparti of the last publication only, leaving out the ones created
 *   inside the draft (BSS-1832). A draft reparto carries a placeholder id until
 *   the changes are made operative in app, and the contact routes of the
 *   Notifiche IoT screen take the numeric id of a persisted reparto. The Gestione
 *   Operativa editor reads without the flag: the draft is exactly what it edits.
 */
export const getGestioneOperativaReparti = (
  aggiornamentiGestioneOperativaId,
  { soloPubblicati = false } = {},
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/reparti${
    soloPubblicati ? '?soloPubblicati=true' : ''
  }`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: reparti non trovati.',
    }),
  );
};

export const updateGestioneOperativaReparti = (
  aggiornamentiGestioneOperativaId,
  body,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/reparti`;
  const URL = BASE_URL + PATH;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PUT',
      headers: {
        Authorization: authtoken,
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      credentials: 'include',
      body: JSON.stringify(body),
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: reparti non trovati.',
    }),
  );
};

export const getGestioneOperativaLocali = (
  aggiornamentiGestioneOperativaId,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/locali`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: locali non trovati.',
    }),
  );
};

export const updateGestioneOperativaLocali = (
  aggiornamentiGestioneOperativaId,
  body,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/locali`;
  const URL = BASE_URL + PATH;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PUT',
      headers: {
        Authorization: authtoken,
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      credentials: 'include',
      body: JSON.stringify(body),
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: locali non trovati.',
    }),
  );
};

export const getGestioneOperativaStrumentazione = (
  aggiornamentiGestioneOperativaId,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/strumentazione`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: strumentazione non trovata.',
    }),
  );
};

export const updateGestioneOperativaStrumentazione = (
  aggiornamentiGestioneOperativaId,
  requestBody,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/strumentazione`;
  const URL = BASE_URL + PATH;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PUT',
      headers: {
        Authorization: authtoken,
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      credentials: 'include',
      body: JSON.stringify(requestBody),
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: strumentazione non trovata.',
    }),
  );
};

/**
 * @param {{soloPubblicate?: boolean}} [opzioni] `soloPubblicate` asks the server
 *   for the attrezzature of the last publication only, leaving out the rows that
 *   belong to the unpublished draft revision (BSS-1573). A draft row is persisted
 *   in the draft and carries a server-assigned UUID id — not the numeric id the
 *   published manual carries — so no Controllo of the published manual hangs off
 *   it and no sonda can feed it. The Gestione Operativa editor reads without the
 *   flag: the draft is exactly what it edits.
 */
export const getGestioneOperativaAttrezzature = (
  aggiornamentiGestioneOperativaId,
  { soloPubblicate = false } = {},
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/attrezzature/gestione-operativa?gestioneOperativaId=${aggiornamentiGestioneOperativaId}${
    soloPubblicate ? '&soloPubblicate=true' : ''
  }`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: attrezzature non trovate.',
    }),
  );
};

export const getGestioneOperativaProcedure = (
  aggiornamentiGestioneOperativaId,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/procedura-haccp`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: procedure non trovate.',
    }),
  );
};

export const getGestioneOperativaProceduraById = (
  aggiornamentiGestioneOperativaId,
  proceduraId,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/procedure-haccp/${proceduraId}`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: procedura non trovata.',
    }),
  );
};

export const updateGestioneOperativaProceduraById = (
  aggiornamentiGestioneOperativaId,
  proceduraId,
  requestBody,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/procedure-haccp/${proceduraId}`;
  const URL = BASE_URL + PATH;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PUT',
      headers: {
        Authorization: authtoken,
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      credentials: 'include',
      body: JSON.stringify(requestBody),
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: procedura non trovata.',
    }),
  );
};

export const updateGestioneOperativaProcedure = (
  aggiornamentiGestioneOperativaId,
  requestBody,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/procedura-haccp`;
  const URL = BASE_URL + PATH;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PUT',
      headers: {
        Authorization: authtoken,
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      credentials: 'include',
      body: JSON.stringify(requestBody),
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: procedure non trovate.',
    }),
  );
};

export const getGestioneOperativaSchede = (
  aggiornamentiGestioneOperativaId,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/schede`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: schede non trovate.',
    }),
  );
};

export const getGestioneOperativaSchedaById = (
  aggiornamentiGestioneOperativaId,
  schedaId,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/schede/${schedaId}`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: scheda non trovata.',
    }),
  );
};

export const updateGestioneOperativaSchedaById = (
  aggiornamentiGestioneOperativaId,
  schedaId,
  requestBody,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/schede/${schedaId}`;
  const URL = BASE_URL + PATH;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PUT',
      headers: {
        Authorization: authtoken,
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      credentials: 'include',
      body: JSON.stringify(requestBody),
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: scheda non trovata.',
    }),
  );
};

export const updateGestioneOperativaSchede = (
  aggiornamentiGestioneOperativaId,
  requestBody,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/schede`;
  const URL = BASE_URL + PATH;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PUT',
      headers: {
        Authorization: authtoken,
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      credentials: 'include',
      body: JSON.stringify(requestBody),
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: schede non trovate.',
    }),
  );
};

export const updateGestioneOperativaControlliSemplificati = (
  aggiornamentiGestioneOperativaId,
  isSemplificata,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/controlli-semplificati?semplificata=${isSemplificata}`;
  const URL = BASE_URL + PATH;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PUT',
      headers: {
        Authorization: authtoken,
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: aggiornamento gestione operativa non trovato.',
    }),
  );
};

export const getControlliSenzaAttrezzatureGestioneOperativa = (
  aggiornamentiGestioneOperativaId,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/controlli-senza-attrezzature`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: controlli non trovati.',
    }),
  );
};

export const pubblicaGestioneOperativa = (
  aggiornamentiGestioneOperativaId,
  incrementaVersione = false,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const basePath = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/pubblicazione`;
  const params = new URLSearchParams({
    incrementaVersione: String(!!incrementaVersione),
  });
  const URL = `${BASE_URL}${basePath}?${params.toString()}`;

  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'PUT',
      headers: {
        Authorization: authtoken,
      },
      credentials: 'include',
    }),
    apiError({
      400: 'Errore durante il caricamento dei dati: gestione operativa non trovata.',
      404: 'Errore durante il caricamento dei dati: gestione operativa non trovata.',
    }),
  );
};

export const getGestioneOperativaStorico = (esercizioId, page, size) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/storico?idEsercizioCommerciale=${esercizioId}&page=${page}&size=${size}`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: storico aggiornamenti non trovato.',
    }),
  );
};

export const getGestioneOperativaStoricoPdf = (aggiornamentoId) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/storico/${aggiornamentoId}/pdf`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: `Aggiornamento con ID ${aggiornamentoId} non trovato.`,
    }),
  );
};

export const replicaGestioneOperativa = (
  aggiornamentiGestioneOperativaId,
  ids,
) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/migrate/${aggiornamentiGestioneOperativaId}`;
  const URL = BASE_URL + PATH;
  return wrapFetchWithDetailedErrors(
    apiFetch(URL, {
      method: 'POST',
      headers: {
        Authorization: authtoken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids }),
      credentials: 'include',
    }),
    apiError({
      400: 'Errore durante il caricamento dei dati: gestione operativa non trovata.',
      404: 'Errore durante il caricamento dei dati: gestione operativa non trovata.',
    }),
  );
};

/**
 * Turns the route's body into `null` (nothing imported yet) or
 * `{ workspace, dataImport, procedure, schede }` (the last import that
 * succeeded). `workspace` is `null` when the imported file's header did not
 * carry the name; a count is `null` when the body does not hold an integer.
 *
 * A body of any other shape throws instead of reading as «nothing imported»: the
 * banner would then invite a first import over a configuration that may exist,
 * and an import replaces the configuration without keeping the old one.
 */
export const leggiImportHaccpAgent = (risposta) => {
  if (risposta?.importata === false) {
    return null;
  }
  if (risposta?.importata === true && typeof risposta.dataImport === 'string') {
    return {
      workspace:
        typeof risposta.workspace === 'string' ? risposta.workspace : null,
      dataImport: risposta.dataImport,
      procedure: Number.isInteger(risposta.procedure)
        ? risposta.procedure
        : null,
      schede: Number.isInteger(risposta.schede) ? risposta.schede : null,
    };
  }
  throw new Error('Risposta di importazione da HACCP Agent non riconosciuta.');
};

/**
 * Whether a configuration from HACCP Agent has been imported into this
 * Gestione Operativa, and from which workspace and when.
 */
export const getImportHaccpAgent = (aggiornamentiGestioneOperativaId) => {
  const token = retrieveToken();
  const authtoken = `Bearer ${token}`;
  const PATH = `/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/import-haccp-agent`;
  const URL = BASE_URL + PATH;
  return wrapFetch(
    apiFetch(URL, {
      method: 'GET',
      headers: {
        Authorization: authtoken,
      },
      withCredentials: true,
      credentials: 'include',
    }),
    apiError({
      404: 'Errore durante il caricamento dei dati: gestione operativa non trovata.',
    }),
  ).then(leggiImportHaccpAgent);
};

/*
 * The import of an Export JSON from HACCP Agent (BSS-1761, BSS-1760): a check
 * that writes nothing, then the write. Both take the file's content as the
 * JSON body, as it is: `{ versione, autore, workspaceId, workspaceNome,
 * categoriePersonalizzate, geop }`.
 *
 * A file the backend refuses is a 400, whose `details` list the defects as
 * sentences; both calls reject with them in `details`, next to the message.
 * No status has a sentence of its own here: the import modal writes the
 * screen for each status itself, and shows the server's sentence only where
 * it adds something.
 */

const erroreImport = (status, serverMessage, details) =>
  Object.assign(apiError()(status, serverMessage), {
    details: Array.isArray(details) ? details : [],
  });

const percorsoImport = (aggiornamentiGestioneOperativaId) =>
  `${BASE_URL}/api/aggiornamenti-gestione-operativa/${aggiornamentiGestioneOperativaId}/import`;

const inviaExport = (url, method, contenuto, signal) =>
  apiFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: contenuto,
    signal,
  });

const interoOppureNull = (valore) => (Number.isInteger(valore) ? valore : null);

const leggiVerificaImport = (risposta) => {
  if (Array.isArray(risposta?.rilievi)) {
    return {
      rilievi: risposta.rilievi,
      workspace:
        typeof risposta.workspace === 'string' && risposta.workspace.trim()
          ? risposta.workspace
          : null,
      procedureDaSostituire: interoOppureNull(risposta.procedureDaSostituire),
      schedeDaSostituire: interoOppureNull(risposta.schedeDaSostituire),
    };
  }
  throw new Error("Risposta di verifica dell'import non riconosciuta.");
};

/**
 * Checks an Export JSON against this GeOp without writing anything. Resolves
 * to `{ rilievi, workspace, procedureDaSostituire, schedeDaSostituire }`: every
 * finding, warnings included, as `{ gravita: 'BLOCCANTE' | 'AVVISO', codice,
 * elemento, messaggio }` — a file with blocking findings still answers 200
 * here — the workspace name the backend read from the header, `null` when it
 * sent none, and how many procedure and schede of the draft the import would
 * replace, `null` when a count did not arrive as an integer.
 */
export const verificaImportHaccpAgent = (
  aggiornamentiGestioneOperativaId,
  contenuto,
  { signal } = {},
) =>
  wrapFetchWithDetailedErrors(
    inviaExport(
      `${percorsoImport(aggiornamentiGestioneOperativaId)}/verifica`,
      'POST',
      contenuto,
      signal,
    ),
    erroreImport,
  ).then(leggiVerificaImport);

/**
 * Writes the configuration of an Export JSON over this GeOp's draft. The
 * backend checks the file again first and writes nothing if it refuses it.
 */
export const applicaImportHaccpAgent = (
  aggiornamentiGestioneOperativaId,
  contenuto,
) =>
  wrapFetchWithDetailedErrors(
    inviaExport(
      percorsoImport(aggiornamentiGestioneOperativaId),
      'PUT',
      contenuto,
    ),
    erroreImport,
  ).then(() => undefined);
